import React, { useState, useEffect, useRef, useCallback } from 'react';
import { StepHelpers } from '@shared/components/Stepper';
import { Button } from '@shared/ui/button';
import { toast } from '@shared/hooks/use-toast';
import { FlatRate, RateComponentRef } from './RateTypes/FlatRate';
import { SlabBased } from './RateTypes/SlabBased';
import { Seasonal } from './RateTypes/Seasonal';
import { TimeOfUse } from './RateTypes/TimeOfUse';
import { FixedRate } from './RateTypes/FixedRate';

interface RateConfigurationProps {
  remoteUtilityId: number;
  storageKey: string;
  logTimeUntilStep?: (step: number | string) => void;
  stepHelpers?: StepHelpers;
  currentStepIndex?: number;
  onNext?: () => void;
  onPrevious?: () => void;
  setValidationError?: (error: string) => void;
  clearValidationError?: () => void;
  isValidating?: boolean;
}

export const RateConfiguration: React.FC<RateConfigurationProps> = ({
  remoteUtilityId,
  storageKey,
  logTimeUntilStep,
  stepHelpers,
  currentStepIndex = 0,
  onNext,
  onPrevious,
  setValidationError,
  clearValidationError,
  isValidating
}) => {
  const dataLoadedRef = useRef(false);
  const rateRef = useRef<RateComponentRef>(null);
  const [formData, setFormData] = useState<any>({});
  const [rateType, setRateType] = useState<number | string | null>(null);
  const basicInfoData = stepHelpers.getStepData(0);

  const saveStepData = useCallback(
    (data: any) => {
      if (!stepHelpers) return;

      try {
        const stepData = {
          ...data,
          timestamp: new Date().toISOString(),
          stepIndex: currentStepIndex,
        };

        stepHelpers.setStepData(currentStepIndex, stepData);
      } catch (error) {
        console.error("Failed to save step data via stepHelpers:", error);
        toast({
          title: "Failed to save progress",
          description: "Your progress may not be saved. Please try again.",
          variant: "destructive",
        });
      }
    },
    [stepHelpers, currentStepIndex]
  );


  const getStepData = useCallback(() => {
    if (!stepHelpers) return null;

    try {
      return stepHelpers.getStepData(currentStepIndex);
    } catch (error) {
      console.error("Failed to get step data via stepHelpers:", error);
      return null;
    }
  }, [stepHelpers, currentStepIndex]);

    useEffect(() => {
    if (stepHelpers && !dataLoadedRef.current) {
      try {
        // 1. Get this specific step's data (Rate Configuration)
        const savedStepData = getStepData();
        if (savedStepData) {
          setFormData(savedStepData);
        }

        // 2. Get data from ALL steps!
        // This returns an object where the keys are the step indexes (0, 1, 2)
        const allStepsData = stepHelpers.getAllStepData();
        console.log("All Steps Data:", allStepsData);

        // Alternatively, if you want a specific previous step's data (e.g., Step 0: Basic Info)
        const basicInfoData = stepHelpers.getStepData(0);
        console.log("Basic Info Data:", basicInfoData);

        // Step 1: Rate Structure data
        const rateStructureData = stepHelpers.getStepData(1);
        if (rateStructureData?.rateType) {
          setRateType(rateStructureData.rateType);
        }
        
        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Failed to load step data:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [stepHelpers, currentStepIndex, getStepData]);

  const renderRateComponent = () => {
    switch (rateType) {
      case 3:
      case "Flat Rate":
        return <FlatRate ref={rateRef} data={formData.rateConfig || {}} unit={basicInfoData?.unit} />;
      case 4:
      case "Slab Based":
        return <SlabBased ref={rateRef} data={formData.rateConfig || {}} unit={basicInfoData?.unit} />;
      case 6:
      case "Seasonal":
        return <Seasonal ref={rateRef} data={formData.rateConfig || {}} unit={basicInfoData?.unit}/>;
      case 5:
      case "Time of Use":
        return <TimeOfUse ref={rateRef} data={formData.rateConfig || {}} unit={basicInfoData?.unit} />;
      case 7:
      case "Fixed":
        return <FixedRate ref={rateRef} data={formData.rateConfig || {}} unit={basicInfoData?.unit} />;
     
      default:
        return (
          <div className="p-8 text-center text-gray-500 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
            Configuration for <strong>{rateType || 'Unknown'}</strong> is coming soon.
          </div>
        );
    }
  };

  const handleContinue = async () => {
    if (clearValidationError) {
      clearValidationError();
    }

    let rateConfigData = null;
    if (rateRef.current) {
      rateConfigData = await rateRef.current.submit();
      if (!rateConfigData) return; // Validation failed, do not continue
    }

    saveStepData({
      ...formData,
      rateConfig: rateConfigData || formData.rateConfig,
      isStepComplete: true,
      completedAt: new Date().toISOString(),
    });

    if (logTimeUntilStep) {
      logTimeUntilStep("Rate Configuration");
    }

    if (onNext) onNext();
  };

  return (
    <div >
      <div className="bg-white">
        {renderRateComponent()}
      </div>

      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={onPrevious}
          disabled={isValidating}
        >
          Previous
        </Button>
        <Button
          onClick={handleContinue}
          disabled={isValidating}
          className="min-w-[120px]"
        >
          Continue
        </Button>
      </div>
    </div>
  );
};
